import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Package, DownloadSimple, CreditCard, CheckCircle, Clock, XCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvoicePage() {
  const { invoiceNumber } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceNumber]);

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/public/${invoiceNumber}`);
      setInvoice(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Invoice not found' : 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    window.open(`${API_URL}/api/invoices/public/${invoiceNumber}/pdf`, '_blank');
  };

  const StatusIcon = ({ status }) => {
    if (status === 'paid') return <CheckCircle size={24} weight="fill" className="text-green-500" />;
    if (status === 'pending') return <Clock size={24} weight="fill" className="text-amber-500" />;
    return <XCircle size={24} weight="fill" className="text-red-500" />;
  };

  const statusLabel = (s) => ({
    pending: 'Pendiente / Pending',
    paid: 'Pagado / Paid',
    overdue: 'Vencido / Overdue',
    cancelled: 'Cancelado / Cancelled'
  }[s] || s);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="invoice-error">
        <div className="text-center">
          <Package size={64} className="mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Factura no encontrada</h1>
          <p className="text-gray-500">Invoice not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-invoice-page">
      {/* Header */}
      <div className="ship-gradient text-white py-6 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-20" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 -mt-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden"
        >
          {/* Invoice Header */}
          <div className="ship-gradient p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-white/70">Factura / Invoice</p>
                <p className="font-mono text-2xl font-bold mt-1" data-testid="invoice-number">{invoice.invoice_number}</p>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                <StatusIcon status={invoice.status} />
                <span className="text-sm font-medium">{statusLabel(invoice.status)}</span>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Customer */}
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Cliente / Customer</p>
              <p className="font-semibold text-lg">{invoice.customer_name}</p>
              <p className="text-sm text-gray-500">{invoice.customer_email}</p>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Fecha / Date</p>
                <p className="text-sm font-medium">{new Date(invoice.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Vencimiento / Due</p>
                <p className="text-sm font-medium">{new Date(invoice.due_date).toLocaleDateString()}</p>
              </div>
            </div>

            {/* Packages */}
            {invoice.packages?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-3">Paquetes / Packages</p>
                <div className="space-y-2">
                  {invoice.packages.map((pkg, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-mono text-xs text-gray-500">{pkg.tracking_number}</p>
                        <p className="text-sm font-medium">{pkg.description}</p>
                        <p className="text-xs text-gray-400">{pkg.weight_lbs} lbs / {pkg.weight_kg} kg</p>
                      </div>
                      <p className="font-bold text-[#0077FF]">${pkg.shipping_cost?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Total USD</span>
                <span className="text-2xl font-bold text-[#0077FF]" data-testid="invoice-total-usd">${invoice.total_usd?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-500">Total CRC</span>
                <span className="text-xl font-bold text-[#FFB300]">₡{invoice.total_crc?.toLocaleString()}</span>
              </div>
              <p className="text-xs text-gray-400 text-right">Tipo de cambio / Rate: ₡{invoice.exchange_rate}</p>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Notas / Notes</p>
                <p className="text-sm text-gray-600">{invoice.notes}</p>
              </div>
            )}

            {/* Paid notice */}
            {invoice.status === 'paid' && invoice.paid_at && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle size={28} weight="fill" className="text-green-500" />
                <div>
                  <p className="font-semibold text-green-800">Pagado / Paid</p>
                  <p className="text-xs text-green-600">{new Date(invoice.paid_at).toLocaleString()}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleDownloadPdf}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#0077FF] hover:bg-[#00796B] text-white rounded-xl font-medium transition-colors"
                data-testid="public-download-pdf"
              >
                <DownloadSimple size={20} />
                Descargar PDF / Download PDF
              </button>
              {invoice.status === 'pending' && invoice.payment_link && (
                <a
                  href={invoice.payment_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#FFB300] hover:bg-[#FFA000] text-white rounded-xl font-medium transition-colors"
                  data-testid="public-pay-now"
                >
                  <CreditCard size={20} />
                  Pagar / Pay Now
                </a>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center border-t">
            <p className="text-xs text-gray-400">
              Track My Package | info@trackmypackage.app
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
